import { get, set } from 'lodash'
import Moment from 'moment'

import { cleanPhone } from './PhoneUtils'

export const isObjectEmpty = obj => {
  if (!obj || typeof obj !== 'object') return true
  return Object.entries(obj).every(([key, val]) => {
    if (key === 'isActual') return true
    if (key === 'lightDuty' && (val === false || val === null || val === undefined)) return true
    if (val === null || val === undefined) return true
    if (typeof val === 'string' && val.trim() === '') return true
    if (Array.isArray(val) && val.length === 0) return true
    if (typeof val === 'object' && Object.keys(val).length === 0) return true
    return false
  })
}

const STATE_MAP = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'district of columbia': 'DC',
  'american samoa': 'AS',
  guam: 'GU',
  'northern mariana islands': 'MP',
  'puerto rico': 'PR',
  'virgin islands': 'VI',
}

export const normalizeStateValue = st => {
  if (!st || typeof st !== 'string') return st
  const cleaned = st.trim().toLowerCase()
  if (cleaned.length === 2) return st.trim().toUpperCase()
  return STATE_MAP[cleaned] || (cleaned.length === 2 ? cleaned.toUpperCase() : st.trim())
}

export const normalizeStatesRecursively = obj => {
  if (!obj || typeof obj !== 'object') return
  Object.keys(obj).forEach(key => {
    if (obj[key] && typeof obj[key] === 'object') {
      normalizeStatesRecursively(obj[key])
    } else if (key.toLowerCase() === 'state' && typeof obj[key] === 'string') {
      obj[key] = normalizeStateValue(obj[key])
    }
  })
}

export const normalizeDateString = val => {
  if (!val || typeof val !== 'string' || val.trim() === '' || val.trim().toLowerCase() === 'null') return null
  const trimmed = val.trim()
  const m = Moment(trimmed, ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM/DD/YY', 'M/D/YYYY', 'M/D/YY', Moment.ISO_8601], true)
  return m.isValid() ? m.format('YYYY-MM-DD') : /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null
}

export const normalizePermissiveDateString = val => {
  if (!val || typeof val !== 'string' || val.trim() === '' || val.trim().toLowerCase() === 'null') return null
  const trimmed = val.trim()
  const m = Moment(trimmed, ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM/DD/YY', 'M/D/YYYY', 'M/D/YY', 'YYYY/MM/DD'], true)
  if (m.isValid()) return m.format('YYYY-MM-DD')
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
  return trimmed
}

export const sanitizeSystemsAndFunctional = caseObj => {
  if (!caseObj || typeof caseObj !== 'object') return caseObj

  if (caseObj.systems && typeof caseObj.systems === 'object') {
    const ALLOWED_SYSTEMS = [
      'general',
      'eyes',
      'skin',
      'nose',
      'head',
      'ears',
      'throat',
      'neurological',
      'cardiac',
      'pulmonary',
      'gigu',
      'musculoskeletal',
    ]
    const cleanSys = {}
    Object.entries(caseObj.systems).forEach(([key, val]) => {
      const lower = key.toLowerCase()
      if (lower === 'musculeskeletal' || lower === 'musculoskeletal') {
        cleanSys.musculoskeletal = cleanSys.musculoskeletal || val
      } else if (ALLOWED_SYSTEMS.includes(lower)) {
        cleanSys[lower] = val
      }
    })
    caseObj.systems = cleanSys
  }

  if (caseObj.functional && typeof caseObj.functional === 'object') {
    const ALLOWED_FUNCTIONAL = [
      'selfcare',
      'adls',
      'behavior',
      'psychological',
      'mobility',
      'limitations',
      'sleep',
      'other',
      'estimatedDateOfMMI',
      'mmiNarrative',
      'bbmanagement',
      'nutrition',
      'medication',
      'cognition',
      'pain',
      'household',
      'communication',
    ]
    const cleanFunc = {}
    Object.entries(caseObj.functional).forEach(([key, val]) => {
      const lower = key.toLowerCase()
      const match = ALLOWED_FUNCTIONAL.find(f => f.toLowerCase() === lower)
      if (match) {
        cleanFunc[match] = val
      }
    })
    caseObj.functional = cleanFunc
  }

  return caseObj
}

/**
 * Normalizes and sanitizes a complete case model prior to saving or schema validation.
 * Prunes empty/stub array items, normalizes dates and phone numbers, standardizes state abbreviations,
 * and fixes legacy typos.
 */
export const cleanAndNormalizeCaseForSave = rawModel => {
  if (!rawModel || typeof rawModel !== 'object') return rawModel
  let model = { ...rawModel }

  // Clean phone numbers
  const phonePaths = [
    'client.phone',
    'client.fax',
    'client.alternateContact.phone',
    'client.alternateContact.fax',
    'employer.contact.phone',
    'employer.contact.fax',
    'plaintiff.contact.phone',
    'plaintiff.contact.fax',
    'defendant.contact.phone',
    'defendant.contact.fax',
    'physicianReviewer.contact.phone',
    'physicianReviewer.contact.fax',
  ]

  phonePaths.forEach(path => {
    const val = get(model, path)
    if (val) {
      model = set(model, path, cleanPhone(val))
    }
  })

  // Clean phone numbers in provider arrays
  const providerArrays = ['treatmentProviders', 'equipmentProviders', 'pharmacies']
  providerArrays.forEach(arrayPath => {
    const list = get(model, arrayPath)
    if (Array.isArray(list)) {
      list.forEach((item, index) => {
        if (item.phone) {
          model = set(model, `${arrayPath}.${index}.phone`, cleanPhone(item.phone))
        }
        if (item.fax) {
          model = set(model, `${arrayPath}.${index}.fax`, cleanPhone(item.fax))
        }
      })
    }
  })

  // Universal empty date normalization (coerces empty/whitespace/null strings to null, ignores snippets and narratives)
  const cleanEmptyDatesRecursively = obj => {
    if (!obj || typeof obj !== 'object') return
    Object.keys(obj).forEach(key => {
      if (obj[key] && typeof obj[key] === 'object') {
        cleanEmptyDatesRecursively(obj[key])
      } else if (typeof obj[key] === 'string') {
        const lowerKey = key.toLowerCase()
        const isIgnored =
          lowerKey.includes('snippet') ||
          lowerKey.includes('narrative') ||
          lowerKey.includes('source') ||
          lowerKey.includes('text') ||
          lowerKey.includes('desc') ||
          lowerKey.includes('note') ||
          lowerKey.includes('reason') ||
          lowerKey.includes('comment')
        if (isIgnored) return
        const isDateField = lowerKey.includes('date') || lowerKey === 'birthdate' || lowerKey === 'dateofhire'
        const isUtcOrTimestamp = lowerKey.endsWith('utc') || lowerKey.endsWith('timestamp')
        if (isDateField && !isUtcOrTimestamp) {
          const trimmed = obj[key].trim()
          if (trimmed === '' || trimmed.toLowerCase() === 'null') {
            obj[key] = null
          }
        }
      }
    })
  }
  cleanEmptyDatesRecursively(model)

  // Strip intake/extraction metadata properties that are unrecognized by the JSON schema
  const stripSourceDocumentRecursively = obj => {
    if (!obj || typeof obj !== 'object') return
    if (Array.isArray(obj)) {
      obj.forEach(item => stripSourceDocumentRecursively(item))
      return
    }
    Object.keys(obj).forEach(key => {
      if (key.toLowerCase() === 'sourcedocument') {
        delete obj[key]
      } else if (obj[key] && typeof obj[key] === 'object') {
        stripSourceDocumentRecursively(obj[key])
      }
    })
  }
  stripSourceDocumentRecursively(model)

  // Strip extraction metadata from sections where additionalProperties: false is enforced
  const stripSectionExtractionMetadata = section => {
    if (!section || typeof section !== 'object') return
    delete section.sourceTextSnippet
    delete section.sourceSnippet
    delete section.page
  }

  if (model.client && typeof model.client === 'object') {
    stripSectionExtractionMetadata(model.client)
    if (model.client.address && typeof model.client.address === 'object') {
      stripSectionExtractionMetadata(model.client.address)
    }
  }

  if (model.employer && typeof model.employer === 'object') {
    stripSectionExtractionMetadata(model.employer)
    if (model.employer.contact && typeof model.employer.contact === 'object') {
      stripSectionExtractionMetadata(model.employer.contact)
    }
    if (model.employer.address && typeof model.employer.address === 'object') {
      stripSectionExtractionMetadata(model.employer.address)
    }
  }

  if (model.mmi && typeof model.mmi === 'object') {
    stripSectionExtractionMetadata(model.mmi)
  }

  if (model.status && typeof model.status === 'object') {
    stripSectionExtractionMetadata(model.status)
    if (Array.isArray(model.status.history)) {
      model.status.history.forEach(item => stripSectionExtractionMetadata(item))
    }
  }

  if (model.referral && typeof model.referral === 'object') {
    stripSectionExtractionMetadata(model.referral)
  }

  // Direct root date fields
  if (model.referral?.date !== undefined) {
    model = set(model, 'referral.date', normalizeDateString(model.referral.date))
  }
  if (model.referral?.dueDate !== undefined) {
    model = set(model, 'referral.dueDate', normalizeDateString(model.referral.dueDate))
  }
  if (model.client?.birthdate !== undefined) {
    model = set(model, 'client.birthdate', normalizePermissiveDateString(model.client.birthdate))
  }
  if (model.client?.dateOfBirth !== undefined) {
    model = set(model, 'client.dateOfBirth', normalizePermissiveDateString(model.client.dateOfBirth))
  }
  if (model.employer?.dateOfHire !== undefined) {
    model = set(model, 'employer.dateOfHire', normalizePermissiveDateString(model.employer.dateOfHire))
  }

  // Injury alias migration, description normalization, date normalization, and unrecognized field stripping
  if (model.injury && typeof model.injury === 'object') {
    if (!model.injury.date) {
      const aliasDate =
        (typeof model.injury.dateOfInjury === 'string' && model.injury.dateOfInjury.trim()) ||
        (typeof model.injury.incidentDate === 'string' && model.injury.incidentDate.trim()) ||
        null
      if (aliasDate) {
        model.injury.date = aliasDate
      }
    }
    if (!model.injury.moi) {
      const aliasMoi =
        (typeof model.injury.mechanism === 'string' && model.injury.mechanism.trim()) ||
        (typeof model.injury.mechanismOfInjury === 'string' && model.injury.mechanismOfInjury.trim()) ||
        null
      if (aliasMoi) {
        model.injury.moi = aliasMoi
      }
    }
    if (!model.injury.noi) {
      const aliasNoi =
        (typeof model.injury.nature === 'string' && model.injury.nature.trim()) ||
        (typeof model.injury.natureOfInjury === 'string' && model.injury.natureOfInjury.trim()) ||
        null
      if (aliasNoi) {
        model.injury.noi = aliasNoi
      }
    }
    if (model.injury.date !== undefined) {
      model = set(model, 'injury.date', normalizeDateString(model.injury.date))
    }

    if (!Array.isArray(model.injury.descriptions)) {
      model.injury.descriptions = []
    }
    if (typeof model.injury.description === 'string' && model.injury.description.trim()) {
      const singleDesc = model.injury.description.trim()
      const exists = model.injury.descriptions.some(
        d => ((typeof d === 'string' ? d : d?.description) || '').trim().toLowerCase() === singleDesc.toLowerCase(),
      )
      if (!exists) {
        model.injury.descriptions.push({
          description: singleDesc,
          provider: 'Report',
        })
      }
      delete model.injury.description
    } else if (model.injury.description !== undefined) {
      delete model.injury.description
    }

    const allowedDescKeys = new Set(['description', 'provider', 'sourceTextSnippet', 'page', 'date'])
    model.injury.descriptions = model.injury.descriptions.map(item => {
      if (typeof item === 'string') {
        const trimmed = item.trim()
        return {
          description: trimmed || null,
          provider: 'Report',
        }
      }
      if (!item || typeof item !== 'object') return item
      const normalizedItem = { ...item }
      if (!normalizedItem.description && typeof normalizedItem.text === 'string' && normalizedItem.text.trim()) {
        normalizedItem.description = normalizedItem.text.trim()
      }
      if (normalizedItem.date !== undefined) {
        normalizedItem.date = normalizePermissiveDateString(normalizedItem.date)
      }
      Object.keys(normalizedItem).forEach(k => {
        if (!allowedDescKeys.has(k)) {
          delete normalizedItem[k]
        }
      })
      return normalizedItem
    })

    const allowedInjuryKeys = new Set([
      'date',
      'descriptions',
      'notes',
      'recordFiles',
      'sourceTextSnippet',
      'dateSourceSnippet',
      'dateSourceTextSnippet',
      'page',
      'moi',
      'noi',
    ])
    Object.keys(model.injury).forEach(k => {
      if (!allowedInjuryKeys.has(k)) {
        delete model.injury[k]
      }
    })
  }

  if (model.mmi?.date !== undefined) {
    model = set(model, 'mmi.date', normalizeDateString(model.mmi.date))
  }
  if (model.mmi?.mmiDate !== undefined) {
    model = set(model, 'mmi.mmiDate', normalizeDateString(model.mmi.mmiDate))
  }
  if (model.functional?.estimatedDateOfMMI !== undefined) {
    model = set(model, 'functional.estimatedDateOfMMI', normalizeDateString(model.functional.estimatedDateOfMMI))
  }
  if (model.lastModifiedDate !== undefined) {
    model = set(model, 'lastModifiedDate', normalizeDateString(model.lastModifiedDate))
  }

  // State abbreviations
  normalizeStatesRecursively(model)

  // Diagnostic tests sanitization & pruning
  if (Array.isArray(model.tests)) {
    model.tests = model.tests
      .filter(t => t && !isObjectEmpty(t))
      .map(t => {
        const rawType = (t.type || t.modality || t.name || '').trim()
        const rawResults = (t.results || '').trim()
        const dateVal = normalizePermissiveDateString(t.date)
        const type = rawType || (rawResults || dateVal ? 'Diagnostic Test' : '')
        return {
          type,
          date: dateVal,
          results: rawResults || null,
        }
      })
      .filter(t => Boolean(t.type))
  }

  // MMI history sanitization & pruning
  if (model.mmi && typeof model.mmi === 'object') {
    if (model.mmi.date) {
      model.mmi.date = normalizeDateString(model.mmi.date)
    }
    if (model.mmi.mmiDate) {
      model.mmi.mmiDate = normalizeDateString(model.mmi.mmiDate)
    }
    let mmiHist = Array.isArray(model.mmi.history) ? [...model.mmi.history] : null
    if (mmiHist === null || mmiHist.length === 0) {
      // History is absent or empty: synthesize from scalar fields if present
      const mmiDateVal = model.mmi.date || model.mmi.mmiDate || model.functional?.estimatedDateOfMMI || null
      const rawReached =
        typeof model.mmi.reached === 'boolean'
          ? model.mmi.reached
          : typeof model.mmi.isMmiReached === 'boolean'
          ? model.mmi.isMmiReached
          : null
      const rating = model.mmi.impairmentRating
      let narrativeVal =
        typeof model.functional?.mmiNarrative === 'string' && model.functional.mmiNarrative.trim()
          ? model.functional.mmiNarrative.trim()
          : null
      if (!narrativeVal && (mmiDateVal || rawReached !== null)) {
        const parts = []
        if (rawReached !== null) {
          parts.push(`MMI: ${rawReached ? 'Reached' : 'Not Reached'}`)
        } else {
          parts.push('MMI Evaluation')
        }
        if (mmiDateVal) {
          parts.push(`on ${mmiDateVal}`)
        }
        if (rating) {
          parts.push(`• Impairment: ${rating}%`)
        }
        narrativeVal = parts.join(' ')
      }
      if (mmiDateVal || rawReached !== null || narrativeVal) {
        mmiHist = [
          {
            date: normalizeDateString(mmiDateVal),
            narrative: narrativeVal || null,
            isActual: rawReached === true,
          },
        ]
      } else {
        mmiHist = []
      }
    }

    const cleanedMmiHist = mmiHist
      .filter(x => x && !isObjectEmpty(x))
      .map(x => {
        const dateVal = normalizeDateString(x.date)
        const narrativeVal = typeof x.narrative === 'string' ? x.narrative.trim() : null
        return {
          date: dateVal,
          narrative: narrativeVal || null,
          isActual: x.isActual === true,
        }
      })
      .filter(x => Boolean(x.date || x.narrative))

    model.mmi = {
      ...model.mmi,
      history: cleanedMmiHist,
    }

    if (cleanedMmiHist.length > 0) {
      const latest = cleanedMmiHist[cleanedMmiHist.length - 1]
      model.mmi.date = latest.date || null
      model.mmi.mmiDate = latest.date || null
      model.mmi.reached = latest.isActual ?? null
      model.mmi.isMmiReached = latest.isActual ?? null
    }
  } else if (!model.mmi && model.functional?.estimatedDateOfMMI) {
    const estDate = normalizeDateString(model.functional.estimatedDateOfMMI)
    const narrativeVal =
      typeof model.functional?.mmiNarrative === 'string' && model.functional.mmiNarrative.trim()
        ? model.functional.mmiNarrative.trim()
        : `MMI Evaluation on ${estDate}`
    model.mmi = {
      date: estDate,
      reached: false,
      isMmiReached: false,
      history: [
        {
          date: estDate,
          narrative: narrativeVal || null,
          isActual: false,
        },
      ],
    }
  }

  // Status history sanitization & pruning
  if (model.status && typeof model.status === 'object') {
    let history = Array.isArray(model.status.history) ? [...model.status.history] : null
    if (history === null || history.length === 0) {
      // History is absent or empty: synthesize from scalar fields if present
      const wsObj = model.workStatus && typeof model.workStatus === 'object' ? model.workStatus : {}
      const cap = model.status.workStatus || wsObj.workCapacity || wsObj.status || null
      const retStatus = model.status.returnStatus || wsObj.restrictions || null
      const effDate = model.status.date || wsObj.effectiveDate || null
      const provider = model.status.treatmentProvider || null
      const treatment = model.status.currentTreatment || null
      const dateLastWorked = model.status.dateLastWorked || null
      const lightDuty = Boolean(
        model.status.lightDuty ||
          (retStatus && /light|desk|modified/i.test(retStatus)) ||
          (cap && /light|desk|modified/i.test(cap)),
      )
      if (cap || retStatus || effDate || provider || treatment || dateLastWorked) {
        history = [
          {
            dateLastWorked,
            date: effDate,
            treatmentProvider: provider,
            currentTreatment: treatment,
            workStatus: cap,
            returnStatus: retStatus,
            lightDuty,
          },
        ]
      } else {
        history = []
      }
    }

    const cleanedHistory = history
      .filter(x => x && !isObjectEmpty(x))
      .map(x => {
        const item = { ...x }
        Object.keys(item).forEach(k => {
          if (typeof item[k] === 'string' && item[k].trim() === '') {
            item[k] = null
          }
        })
        if (item.dateLastWorked !== undefined) {
          item.dateLastWorked = normalizePermissiveDateString(item.dateLastWorked)
        }
        if (item.date !== undefined) {
          item.date = normalizePermissiveDateString(item.date)
        }
        item.lightDuty = Boolean(item.lightDuty)
        return item
      })

    model.status = {
      ...model.status,
      history: cleanedHistory,
    }

    if (cleanedHistory.length > 0) {
      const latest = cleanedHistory[cleanedHistory.length - 1]
      model.status.workStatus = latest.workStatus ?? null
      model.status.returnStatus = latest.returnStatus ?? null
      model.status.date = latest.date ?? null
      model.status.treatmentProvider = latest.treatmentProvider ?? null
      model.status.currentTreatment = latest.currentTreatment ?? null
      model.status.dateLastWorked = latest.dateLastWorked ?? null
      model.status.lightDuty = Boolean(latest.lightDuty)
    }
  } else if (!model.status && model.workStatus && typeof model.workStatus === 'object') {
    const wsObj = model.workStatus
    const cap = wsObj.workCapacity || wsObj.status || null
    const retStatus = wsObj.restrictions || null
    const effDate = wsObj.effectiveDate || null
    if (cap || retStatus || effDate) {
      const isLightDuty = Boolean(
        (retStatus && /light|desk|modified/i.test(retStatus)) || (cap && /light|desk|modified/i.test(cap)),
      )
      model.status = {
        workStatus: cap,
        returnStatus: retStatus,
        date: effDate,
        lightDuty: isLightDuty,
        history: [
          {
            dateLastWorked: null,
            date: effDate,
            treatmentProvider: null,
            currentTreatment: null,
            workStatus: cap,
            returnStatus: retStatus,
            lightDuty: isLightDuty,
          },
        ],
      }
    }
  }

  // Allergies sanitization & pruning
  if (Array.isArray(model.allergies)) {
    model.allergies = model.allergies
      .map(a => {
        if (!a) return null
        if (typeof a === 'string') {
          const trimmed = a.trim()
          return trimmed ? { name: trimmed } : null
        }
        const name = (a.name || a.allergy || a.description || '').trim()
        if (!name) return null
        return {
          name,
          reaction: (a.reaction || '').trim() || null,
        }
      })
      .filter(Boolean)
  }

  // Medications
  if (Array.isArray(model.medications)) {
    model.medications = model.medications
      .filter(m => m && typeof m.name === 'string' && m.name.trim() !== '')
      .map(m => {
        const item = { ...m }
        if (typeof item.dateStarted === 'string' && item.dateStarted.trim() === '') item.dateStarted = null
        if (typeof item.dateDiscontinued === 'string' && item.dateDiscontinued.trim() === '')
          item.dateDiscontinued = null
        if (item.dateStarted) item.dateStarted = normalizeDateString(item.dateStarted)
        if (item.dateDiscontinued) item.dateDiscontinued = normalizeDateString(item.dateDiscontinued)
        return item
      })
  }

  // Surgeries & hospitalizations
  if (Array.isArray(model.surgeriesAndHospitalizations)) {
    model.surgeriesAndHospitalizations = model.surgeriesAndHospitalizations
      .filter(s => s && !isObjectEmpty(s))
      .map(s => {
        const item = { ...s }
        if (item.dateOfSurgery !== undefined) item.dateOfSurgery = normalizeDateString(item.dateOfSurgery)
        if (item.dateAdmitted !== undefined) item.dateAdmitted = normalizeDateString(item.dateAdmitted)
        if (item.dateDischarged !== undefined) item.dateDischarged = normalizeDateString(item.dateDischarged)
        return item
      })
  }

  // Diagnoses
  if (Array.isArray(model.diagnosis)) {
    model.diagnosis = model.diagnosis.filter(d => d && typeof d.name === 'string' && d.name.trim() !== '')
  }

  // Equipment & Home setup
  if (Array.isArray(model.equipment)) {
    model.equipment = model.equipment
      .filter(e => e && typeof e.item === 'string' && e.item.trim() !== '')
      .map(e => {
        const item = { ...e }
        if (item.datePurchased !== undefined) item.datePurchased = normalizeDateString(item.datePurchased)
        if (item.dateReceived !== undefined) item.dateReceived = normalizeDateString(item.dateReceived)
        if (item.dateRepaired !== undefined) item.dateRepaired = normalizeDateString(item.dateRepaired)
        if (item.dateWarranty !== undefined) item.dateWarranty = normalizeDateString(item.dateWarranty)
        if (item.rentalStartDate !== undefined) item.rentalStartDate = normalizeDateString(item.rentalStartDate)
        if (item.rentalEndDate !== undefined) item.rentalEndDate = normalizeDateString(item.rentalEndDate)
        return item
      })
  }
  if (Array.isArray(model.homeSetup)) {
    model.homeSetup = model.homeSetup.filter(h => h && typeof h.type === 'string' && h.type.trim() !== '')
  }

  // Providers
  if (Array.isArray(model.treatmentProviders)) {
    model.treatmentProviders = model.treatmentProviders.filter(
      p => p && typeof p.name === 'string' && p.name.trim() !== '',
    )
  }
  if (Array.isArray(model.equipmentProviders)) {
    model.equipmentProviders = model.equipmentProviders.filter(
      p => p && typeof p.name === 'string' && p.name.trim() !== '',
    )
  }
  if (Array.isArray(model.pharmacies)) {
    model.pharmacies = model.pharmacies.filter(p => p && typeof p.name === 'string' && p.name.trim() !== '')
  }

  // Enforce allowed properties on sections with additionalProperties: false in CaseSchema.json
  const stripDisallowedKeys = (target, allowedSet) => {
    if (!target || typeof target !== 'object') return
    Object.keys(target).forEach(k => {
      if (!allowedSet.has(k)) {
        delete target[k]
      }
    })
  }
  const allowedAddressKeys = new Set(['line1', 'city', 'state', 'zip'])
  const allowedContactKeys = new Set(['firstName', 'lastName', 'title', 'phone', 'email', 'fax'])
  const allowedAlternateContactKeys = new Set(['firstName', 'lastName', 'phone', 'fax', 'email'])
  const allowedClientKeys = new Set([
    'firstName',
    'lastName',
    'address',
    'phone',
    'fax',
    'email',
    'birthdate',
    'dateOfBirth',
    'firstLanguage',
    'secondLanguage',
    'hasAlternateContact',
    'alternateContact',
    'occupation',
  ])
  const allowedEmployerKeys = new Set(['name', 'address', 'contact', 'dateOfHire', 'occupation'])
  const allowedReferralKeys = new Set([
    'date',
    'sourceId',
    'adjusterId',
    'claimNumber',
    'policyNumber',
    'dueDate',
    'referralType',
    'insuranceCompany',
  ])
  const allowedMmiKeys = new Set([
    'reached',
    'isMmiReached',
    'date',
    'mmiDate',
    'dateUnknown',
    'impairmentRating',
    'history',
  ])
  const allowedStatusKeys = new Set([
    'date',
    'treatmentProvider',
    'dateLastWorked',
    'currentTreatment',
    'workStatus',
    'returnStatus',
    'lightDuty',
    'history',
    'sourceTextSnippet',
    'page',
  ])

  if (model.client && typeof model.client === 'object') {
    stripDisallowedKeys(model.client, allowedClientKeys)
    stripDisallowedKeys(model.client.alternateContact, allowedAlternateContactKeys)
  }
  if (model.employer && typeof model.employer === 'object') {
    stripDisallowedKeys(model.employer, allowedEmployerKeys)
    stripDisallowedKeys(model.employer.address, allowedAddressKeys)
    stripDisallowedKeys(model.employer.contact, allowedContactKeys)
  }
  if (model.referral && typeof model.referral === 'object') {
    stripDisallowedKeys(model.referral, allowedReferralKeys)
  }
  if (model.mmi && typeof model.mmi === 'object') {
    stripDisallowedKeys(model.mmi, allowedMmiKeys)
  }
  if (model.status && typeof model.status === 'object') {
    stripDisallowedKeys(model.status, allowedStatusKeys)
    if (Array.isArray(model.status.history)) {
      model.status.history.forEach(h => stripDisallowedKeys(h, allowedStatusKeys))
    }
  }
  ;['treatmentProviders', 'equipmentProviders', 'pharmacies'].forEach(arrKey => {
    if (Array.isArray(model[arrKey])) {
      model[arrKey].forEach(p => {
        if (p && typeof p.address === 'object') {
          stripDisallowedKeys(p.address, allowedAddressKeys)
        }
      })
    }
  })

  // Systems & Functional cleanup
  model = sanitizeSystemsAndFunctional(model)

  return model
}
